"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useSession } from "next-auth/react";
import { useToast } from "@/context/ToastContext";
import { trackEvent } from "@/lib/track";

interface WishlistContextValue {
  wishlistIds: Set<string>;
  wishlistCount: number;
  isInWishlist: (productId: string) => boolean;
  toggleWishlist: (productId: string) => void;
  isLoading: boolean;
}

const WishlistContext = createContext<WishlistContextValue | null>(null);

export function WishlistProvider({ children }: { children: ReactNode }) {
  const { data: session, status } = useSession();
  const { toast } = useToast();
  const [wishlistIds, setWishlistIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // Load wishlist on mount when user is logged in
  useEffect(() => {
    if (status !== "authenticated") {
      setWishlistIds(new Set());
      return;
    }

    setIsLoading(true);
    fetch("/api/wishlist")
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((items: { productId: string }[]) => {
        setWishlistIds(new Set(items.map((i) => i.productId)));
      })
      .catch(() => {
        // Silently fail — wishlist will just appear empty
      })
      .finally(() => setIsLoading(false));
  }, [status]);

  const isInWishlist = useCallback(
    (productId: string) => wishlistIds.has(productId),
    [wishlistIds]
  );

  const toggleWishlist = useCallback(
    async (productId: string) => {
      if (status !== "authenticated" || !session?.user) {
        toast("Please log in to save to wishlist", "info");
        return;
      }

      const isCurrentlyInWishlist = wishlistIds.has(productId);

      // Optimistic update
      trackEvent("wishlist_toggled", {
        productId,
        action: isCurrentlyInWishlist ? "removed" : "added",
      });
      setWishlistIds((prev) => {
        const next = new Set(prev);
        if (isCurrentlyInWishlist) {
          next.delete(productId);
        } else {
          next.add(productId);
        }
        return next;
      });

      try {
        let res: Response;
        if (isCurrentlyInWishlist) {
          res = await fetch(
            `/api/wishlist?productId=${encodeURIComponent(productId)}`,
            { method: "DELETE" }
          );
        } else {
          res = await fetch("/api/wishlist", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ productId }),
          });
        }
        if (!res.ok) {
          // Audit Section 5 — distinguish a deleted-product 404 from a
          // generic server error so users with stale wishlists get a
          // meaningful message instead of "Something went wrong".
          if (res.status === 404) {
            // Drop it from local state so the heart doesn't bounce back.
            setWishlistIds((prev) => {
              const next = new Set(prev);
              next.delete(productId);
              return next;
            });
            toast("Product no longer available — removed from wishlist", "info");
            return;
          }
          throw new Error();
        }
      } catch {
        // Revert optimistic update
        setWishlistIds((prev) => {
          const reverted = new Set(prev);
          if (isCurrentlyInWishlist) {
            reverted.add(productId);
          } else {
            reverted.delete(productId);
          }
          return reverted;
        });
        toast("Something went wrong", "error");
      }
    },
    [status, session, wishlistIds, toast]
  );

  return (
    <WishlistContext.Provider
      value={{
        wishlistIds,
        wishlistCount: wishlistIds.size,
        isInWishlist,
        toggleWishlist,
        isLoading,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx)
    throw new Error("useWishlist must be used within WishlistProvider");
  return ctx;
}
